import React from "react";

export default function PrintableReportCard({ card, reportCards }) {
  const cardsToRender = reportCards 
    ? reportCards 
    : card 
    ? [card] 
    : [];

  if (!cardsToRender.length) return null;

  return (
    <div id="printable-report-card-area">
      {/* Print-specific Styles */}
      <style>{`
        @media print {
          @page {
            size: A4 portrait;
            margin: 0;
          }

          body * {
            visibility: hidden !important;
          }
          table, th, td {
            border: 0.5pt solid #000000 !important;
            border-collapse: collapse !important;
          }
          #printable-report-card-area,
          #printable-report-card-area * {
            visibility: visible !important;
          }

          #printable-report-card-area {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
          }

          body {
            background: white !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          .page-card {
            position: relative !important;
            padding: 12mm 15mm !important;
            height: 297mm !important;
            max-height: 297mm !important;
            page-break-after: always !important;
            break-after: page !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            overflow: hidden !important;
          }
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .no-print {
            display: none !important;
          }

          .watermark-container {
            position: absolute !important;
            top: 50% !important;
            left: 50% !important;
            transform: translate(-50%, -50%) !important;
            width: 100% !important;
            display: flex !important;
            justify-content: center !important;
            align-items: center !important;
            z-index: 0 !important;
          }
        }
      `}</style>

      {/* Print Button Trigger */}
      <div className="no-print mb-4 flex justify-end">
        <button
          onClick={() => window.print()}
          className="px-4 py-2 bg-zinc-900 text-white rounded-lg text-xs font-semibold hover:bg-zinc-800 transition-colors shadow-sm"
        >
          Print Report Cards
        </button>
      </div>

      {/* Report Cards Rendering Loop */}
      {cardsToRender.map((reportCard, idx) => (
        <div
          key={reportCard.student_id || idx}
          className="page-card relative bg-white text-black p-4 max-w-[210mm] mx-auto text-[8.5px] leading-tight font-sans text-zinc-900 antialiased"
          style={{
            fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif"
          }}
        >
          {/* Background Watermark Logo (Hidden in screen preview, visible only during Print/Download) */}
          <div className="watermark-container hidden print:flex pointer-events-none select-none z-0 opacity-10">
            <img
              src={reportCard.school_logo || "/logo.png"}
              alt="School Watermark"
              className="w-[100mm] max-w-[60%] object-contain grayscale"
            />
          </div>

          {/* Report Card Content Layer (Stacked above watermark) */}
          <div className="relative z-10">
            {/* 1. Header Section */}
            <div className="text-center border-b pb-2 mb-2 border-zinc-300">
              <img
                src={reportCard.school_logo || "/logo.png"}
                alt="School Logo"
                className="w-20 h-20 object-contain mx-auto mb-1"
              />
              <h1 className="text-xs font-extrabold uppercase tracking-wide text-zinc-900">
                {reportCard.school_name || "Cambodia Adventist International School"}
              </h1>
              <h2 className="text-[10px] font-bold tracking-wider text-zinc-800 mt-0.5">
                REPORT CARD
              </h2>
              <p className="text-[8.5px] font-medium text-zinc-600 mt-0.5">
                SY {reportCard.school_year || "2025-2026"} &bull; {reportCard.term || "Quarter 1"}
              </p>
              <p className="text-[8px] text-zinc-500 mt-0.5">
                {reportCard.school_address || "No. 419, Rada Street, Phum Tum Nub, Sangkat Phnom Penh Thmei, Khan Saen Sokh (120801), Phnom Penh Capital, Kingdom of Cambodia"}
              </p>
            </div>

            {/* 2. Student Info Grid */}
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 mb-2 bg-zinc-50/80 p-2 rounded text-[8.5px]">
              <div>
                <span className="font-bold text-zinc-700">Student Name: </span>
                <span className="font-semibold text-zinc-900">{reportCard.student_name}</span>
              </div>
              <div>
                <span className="font-bold text-zinc-700">Student ID: </span>
                <span className="font-semibold text-zinc-900">{reportCard.student_id_number}</span>
              </div>
              <div>
                <span className="font-bold text-zinc-700">Grade Level: </span>
                <span className="font-semibold text-zinc-900">{reportCard.grade_level}</span>
              </div>
              <div>
                <span className="font-bold text-zinc-700">Homeroom Teacher: </span>
                <span className="font-semibold text-zinc-900">{reportCard.homeroom_teacher}</span>
              </div>
            </div>

            {/* 3. Grades & Conduct Table */}
            <table className="w-full border-collapse border-[0.5px] border-zinc-900 text-[8.5px] mb-2 font-sans text-black">
              <thead>
                {/* Main Header Row */}
                <tr className="bg-zinc-100 text-center font-bold text-zinc-900 uppercase tracking-tight">
                  <th className="border border-zinc-800 p-1 text-left w-[22%]" rowSpan={2}>
                    COURSE NAME
                  </th>
                  <th className="border border-zinc-800 p-1" colSpan={3}>
                    SEMESTER 1
                  </th>
                  <th className="border border-zinc-800 p-1" colSpan={3}>
                    SEMESTER 2
                  </th>
                  <th className="border border-zinc-800 p-1 w-[8%]" rowSpan={2}>
                    FINAL
                  </th>
                  <th className="border border-zinc-800 p-1" colSpan={4}>
                    ATTENDANCE
                  </th>
                  <th className="border border-zinc-800 p-1 text-center w-[20%]" rowSpan={2}>
                    CONDUCT
                  </th>
                </tr>

                {/* Sub-Header Row */}
                <tr className="bg-zinc-100 text-center font-bold text-zinc-900">
                  <th className="border border-zinc-800 p-0.5 w-[6%]">1</th>
                  <th className="border border-zinc-800 p-0.5 w-[6%]">2</th>
                  <th className="border border-zinc-800 p-0.5 w-[7%]">GRADE</th>
                  <th className="border border-zinc-800 p-0.5 w-[6%]">3</th>
                  <th className="border border-zinc-800 p-0.5 w-[6%]">4</th>
                  <th className="border border-zinc-800 p-0.5 w-[7%]">GRADE</th>
                  <th className="border border-zinc-800 p-0.5 w-[4%]">AE</th>
                  <th className="border border-zinc-800 p-0.5 w-[4%]">AU</th>
                  <th className="border border-zinc-800 p-0.5 w-[4%]">TE</th>
                  <th className="border border-zinc-800 p-0.5 w-[4%]">TU</th>
                </tr>
              </thead>

              <tbody>
                {reportCard.courses?.map((course, cIdx) => (
                  <React.Fragment key={cIdx}>
                    {/* Main Data Row */}
                    <tr className="text-center align-middle font-semibold text-black">
                      <td className="border-[0.5px] border-zinc-800 p-1 text-left font-semibold text-widest text-zinc-900">
                        {course.name}
                      </td>
                      <td className="border-[0.5px] border-zinc-800 p-1">{course.q1 || "-"}</td>
                      <td className="border-[0.5px] border-zinc-800 p-1">{course.q2 || "-"}</td>
                      <td className="border-[0.5px] border-zinc-800 p-1 font-bold bg-zinc-50">
                        {course.sem1_grade || "-"}
                      </td>
                      <td className="border-[0.5px] border-zinc-800 p-1">{course.q3 || "-"}</td>
                      <td className="border-[0.5px] border-zinc-800 p-1">{course.q4 || "-"}</td>
                      <td className="border-[0.5px] border-zinc-800 p-1 font-bold bg-zinc-50">
                        {course.sem2_grade || "-"}
                      </td>
                      <td className="border-[0.5px] border-zinc-800 p-1 font-extrabold text-zinc-900">
                        {course.final_grade || "-"}
                      </td>
                      <td className="border-[0.5px] border-zinc-800 p-0.5">{course.ae ?? 0}</td>
                      <td className="border-[0.5px] border-zinc-800 p-0.5">{course.au ?? 0}</td>
                      <td className="border-[0.5px] border-zinc-800 p-0.5">{course.te ?? 0}</td>
                      <td className="border-[0.5px] border-zinc-800 p-0.5">{course.tu ?? 0}</td>
                      <td className="border-[0.5px] border-zinc-800 p-1 text-center font-medium">
                        {course.conduct || "Satisfactory"}
                      </td>
                    </tr>

                    {/* Teacher Comment Row (Optional below course) */}
                    {(course.teacher_name || course.teacher_comment) && (
                      <tr className="bg-gray-100">
                        <td colSpan={13} className="border border-zinc-800 px-2 py-0.5 text-left">
                          <div className="flex items-center gap-2 text-[7.5px] italic text-zinc-800">
                            {course.teacher_name && (
                              <span className="font-semibold not-italic text-zinc-900">
                                {course.teacher_name}:
                              </span>
                            )}
                            <span>"{course.teacher_comment}"</span>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>

            {/* Official Document Notice */}
            <p className="text-[7px] text-center font-bold uppercase tracking-wider text-red-600 my-1">
              This is an official document only if there is a red CAIS stamp and signature below
            </p>

            {/* 4. Grade Scale & Attendance Section */}
            <div className="grid grid-cols-2 gap-2 text-[7.5px] mb-2">
              {/* Grade Scale Box */}
              <div className="border border-black p-1.5 bg-zinc-50/40 space-y-0.5">
                <p className="font-bold border-b border-zinc-200 pb-0.5 text-zinc-800 uppercase tracking-wide">
                  Grade Scale
                </p>
                <div className="grid grid-cols-2 gap-x-1 gap-y-0.5 text-zinc-600">
                  <div><span className="font-bold text-zinc-800">Excellent A:</span> 93-100 (4.00)</div>
                  <div><span className="font-bold text-zinc-800">A-:</span> 90-92 (3.70)</div>
                  <div><span className="font-bold text-zinc-800">Good B+:</span> 87-89 (3.30)</div>
                  <div>
                    <span className="font-bold text-zinc-800">B:</span> 83-86 | <span className="font-bold text-zinc-800">B-:</span> 80-82
                  </div>
                  <div><span className="font-bold text-zinc-800">Satisfactory C+:</span> 77-79</div>
                  <div>
                    <span className="font-bold text-zinc-800">C:</span> 73-76 | <span className="font-bold text-zinc-800">C-:</span> 70-72
                  </div>
                  <div><span className="font-bold text-zinc-800">Poor D+:</span> 67-69</div>
                  <div>
                    <span className="font-bold text-zinc-800">D:</span> 63-66 | <span className="font-bold text-zinc-800">D-:</span> 60-62
                  </div>
                  <div><span className="font-bold text-zinc-800">Failed F:</span> 0-59</div>
                </div>

                <div className="pt-1 border-t border-zinc-200 mt-0.5 flex gap-2 text-zinc-700 text-[7px]">
                  <span className="font-bold">Weights:</span>
                  <span>Daily = 35%</span>
                  <span>Tests = 35%</span>
                  <span>Exam = 30%</span>
                </div>
              </div>

              {/* Attendance Box */}
              <div className="space-y-1">
                <table className="w-full border-collapse border border-zinc-300 text-center text-[7.5px]">
                  <thead>
                    <tr className="bg-zinc-100/90 font-bold">
                      <th className="border border-zinc-300 p-0.5 text-left">Attendance</th>
                      <th className="border border-zinc-300 p-0.5 w-5">1</th>
                      <th className="border border-zinc-300 p-0.5 w-5">2</th>
                      <th className="border border-zinc-300 p-0.5 w-5">3</th>
                      <th className="border border-zinc-300 p-0.5 w-5">4</th>
                      <th className="border border-zinc-300 p-0.5 w-8">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-zinc-300 p-0.5 text-left font-semibold">School Days</td>
                      <td className="border border-zinc-300 p-0.5">{reportCard.attendance?.q1_days || 45}</td>
                      <td className="border border-zinc-300 p-0.5">{reportCard.attendance?.q2_days || 41}</td>
                      <td className="border border-zinc-300 p-0.5">{reportCard.attendance?.q3_days || 38}</td>
                      <td className="border border-zinc-300 p-0.5">{reportCard.attendance?.q4_days || 52}</td>
                      <td className="border border-zinc-300 p-0.5 font-bold">{reportCard.attendance?.total_days || 176}</td>
                    </tr>
                    <tr>
                      <td className="border border-zinc-300 p-0.5 text-left font-semibold">Days Present</td>
                      <td className="border border-zinc-300 p-0.5">{reportCard.attendance?.q1_present || 44}</td>
                      <td className="border border-zinc-300 p-0.5">{reportCard.attendance?.q2_present || 39}</td>
                      <td className="border border-zinc-300 p-0.5">{reportCard.attendance?.q3_present || 38}</td>
                      <td className="border border-zinc-300 p-0.5">{reportCard.attendance?.q4_present || 52}</td>
                      <td className="border border-zinc-300 p-0.5 font-bold">{reportCard.attendance?.total_present || 173}</td>
                    </tr>
                    <tr>
                      <td className="border border-zinc-300 p-0.5 text-left font-semibold">Days Absent</td>
                      <td className="border border-zinc-300 p-0.5">{reportCard.attendance?.q1_absent || 1}</td>
                      <td className="border border-zinc-300 p-0.5">{reportCard.attendance?.q2_absent || 2}</td>
                      <td className="border border-zinc-300 p-0.5">{reportCard.attendance?.q3_absent || 0}</td>
                      <td className="border border-zinc-300 p-0.5">{reportCard.attendance?.q4_absent || 0}</td>
                      <td className="border border-zinc-300 p-0.5 font-bold">{reportCard.attendance?.total_absent || 3}</td>
                    </tr>
                    <tr>
                      <td className="border border-zinc-300 p-0.5 text-left font-semibold">Times Tardy</td>
                      <td className="border border-zinc-300 p-0.5">{reportCard.attendance?.q1_tardy || 0}</td>
                      <td className="border border-zinc-300 p-0.5">{reportCard.attendance?.q2_tardy || 0}</td>
                      <td className="border border-zinc-300 p-0.5">{reportCard.attendance?.q3_tardy || 0}</td>
                      <td className="border border-zinc-300 p-0.5">{reportCard.attendance?.q4_tardy || 0}</td>
                      <td className="border border-zinc-300 p-0.5 font-bold">{reportCard.attendance?.total_tardy || 0}</td>
                    </tr>
                  </tbody>
                </table>

                <div className="border border-black p-1 bg-zinc-50/40 text-[7px] leading-tight space-y-0.5">
                  <p><span className="font-bold">E / EE:</span> Exceeds expectation | <span className="font-bold">M / MM:</span> Meets expectations</p>
                  <p><span className="font-bold">W / WW:</span> Working toward | <span className="font-bold">IR:</span> Intervention required</p>
                </div>
              </div>
            </div>

            {/* 5. Signatures Footer */}
            <div className="mt-4 pt-12 flex justify-between items-end">
              <div className="text-center w-40">
                <div className="border-b border-zinc-800 mb-0.5"></div>
                <p className="font-bold text-[8px]">{reportCard.registrar_name || "Chin Chariya"}</p>
                <p className="text-[7.5px] text-zinc-500">Registrar</p>
              </div>

              <div className="text-center text-[7.5px] text-zinc-400">
                <p>Date Printed: {reportCard.date_printed || new Date().toLocaleDateString("en-GB")}</p>
              </div>

              <div className="text-center w-40">
                <div className="border-b border-zinc-800 mb-0.5"></div>
                <p className="font-bold text-[8px]">{reportCard.homeroom_teacher}</p>
                <p className="text-[7.5px] text-zinc-500">Homeroom Teacher</p>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}